import { Cluster, Unit, Zone } from "@ses-mams/api-contract";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  FilterPopover,
  FilterPopoverFooter,
  FilterPopoverHeader,
} from "~/components/common/filterPopover";
import { Stack } from "~/components/ui/stack";
import { EditListSection } from "~/components/common/editListSection";
import { ZoneListItem } from "~/components/common/zone";
import { UnitListItem } from "~/components/common/unit";
import { ClusterListItem } from "~/components/common/cluster";
import { SelectZonesDrawer } from "~/components/common/selectZonesList";
import { SelectClustersDrawer } from "~/components/common/selectClustersList";
import { SelectUnitsDrawer } from "~/components/common/selectUnitsList";
import { tsr } from "~/utils/client";

export const SearchUsersFilters = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [isSelectZonesDrawerOpen, setIsSelectZonesDrawerOpen] = useState(false);
  const [isSelectClustersDrawerOpen, setIsSelectClustersDrawerOpen] =
    useState(false);
  const [isSelectUnitsDrawerOpen, setIsSelectUnitsDrawerOpen] = useState(false);

  const {
    units,
    clusters,
    zones,
    setUnits,
    setClusters,
    setZones,
    initialFilters,
  } = useSearchFilters();

  const [internalFilters, setInternalFilters] = useState(() => ({
    units,
    clusters,
    zones,
  }));

  const resetFilters = useCallback(() => {
    setInternalFilters(initialFilters);
  }, [initialFilters]);

  const clearFilters = useCallback(() => {
    setInternalFilters({ units: [], clusters: [], zones: [] });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setUnits(internalFilters.units);
      setClusters(internalFilters.clusters);
      setZones(internalFilters.zones);

      setIsOpen(false);
    },
    [internalFilters]
  );

  return (
    <FilterPopover
      open={isOpen}
      onOpenChange={setIsOpen}
      // Ensure that the select drawers appear on top of the popover
      sx={theme => ({ zIndex: theme.zIndex.drawer - 1 })}
    >
      <form onSubmit={handleSubmit}>
        <FilterPopoverHeader onReset={resetFilters} onClear={clearFilters} />

        <Stack gap="large">
          <EditListSection
            title="Units"
            onButtonClick={() => setIsSelectUnitsDrawerOpen(true)}
          >
            {internalFilters.units.map(unit => {
              return <UnitListItem key={unit.id} unit={unit} />;
            })}
          </EditListSection>

          <EditListSection
            title="Clusters"
            onButtonClick={() => setIsSelectClustersDrawerOpen(true)}
          >
            {internalFilters.clusters.map(cluster => {
              return <ClusterListItem key={cluster.id} cluster={cluster} />;
            })}
          </EditListSection>

          <EditListSection
            title="Zones"
            onButtonClick={() => setIsSelectZonesDrawerOpen(true)}
          >
            {internalFilters.zones.map(zone => {
              return <ZoneListItem key={zone.id} zone={zone} />;
            })}
          </EditListSection>
        </Stack>

        <FilterPopoverFooter onClose={() => setIsOpen(false)} />
      </form>

      <SelectZonesDrawer
        open={isSelectZonesDrawerOpen}
        zones={internalFilters.zones}
        onClose={() => setIsSelectZonesDrawerOpen(false)}
        onDone={value =>
          setInternalFilters(prev => ({ ...prev, zones: value }))
        }
      />

      <SelectClustersDrawer
        open={isSelectClustersDrawerOpen}
        clusters={internalFilters.clusters}
        onClose={() => setIsSelectClustersDrawerOpen(false)}
        onDone={value =>
          setInternalFilters(prev => ({ ...prev, clusters: value }))
        }
      />

      <SelectUnitsDrawer
        open={isSelectUnitsDrawerOpen}
        units={internalFilters.units}
        onClose={() => setIsSelectUnitsDrawerOpen(false)}
        onDone={value =>
          setInternalFilters(prev => ({ ...prev, units: value }))
        }
      />
    </FilterPopover>
  );
};

type SearchUsersFilters = {
  units: Array<Unit>;
  clusters: Array<Cluster>;
  zones: Array<Zone>;
};

type SearchFiltersContextType = SearchUsersFilters & {
  setUnits: React.Dispatch<React.SetStateAction<SearchUsersFilters["units"]>>;
  setClusters: React.Dispatch<
    React.SetStateAction<SearchUsersFilters["clusters"]>
  >;
  setZones: React.Dispatch<React.SetStateAction<SearchUsersFilters["zones"]>>;
  initialFilters: SearchUsersFilters;
};

const SearchFiltersContext = createContext<SearchFiltersContextType>({
  units: [],
  clusters: [],
  zones: [],
  setUnits: () => null,
  setClusters: () => null,
  setZones: () => null,
  initialFilters: {
    units: [],
    clusters: [],
    zones: [],
  },
});

export const SearchFiltersContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { data: unitsData } = tsr.units.list.useSuspenseQuery({
    queryKey: ["units", { memberUnitsOnly: true }],
    queryData: {
      query: {
        memberUnitsOnly: true,
      },
    },
  });

  const { data: clustersData } = tsr.clusters.list.useSuspenseQuery({
    queryKey: ["clusters", { memberClustersOnly: true }],
    queryData: {
      query: {
        memberClustersOnly: true,
      },
    },
  });

  const { data: zonesData } = tsr.zones.list.useSuspenseQuery({
    queryKey: ["zones", { memberZonesOnly: true }],
    queryData: {
      query: {
        memberZonesOnly: true,
      },
    },
  });

  const initialFilters = useMemo(() => {
    return {
      units: unitsData?.body.items || [],
      clusters: [],
      zones: [],
    };
  }, [unitsData, clustersData, zonesData]);

  const [units, setUnits] = useState<SearchUsersFilters["units"]>(
    initialFilters.units
  );
  const [clusters, setClusters] = useState<SearchUsersFilters["clusters"]>(
    initialFilters.clusters
  );
  const [zones, setZones] = useState<SearchUsersFilters["zones"]>(
    initialFilters.zones
  );

  const resetFilters = useCallback(() => {
    setUnits(initialFilters.units);
    setClusters(initialFilters.clusters);
    setZones(initialFilters.zones);
  }, [setUnits, setClusters, setZones, initialFilters]);

  const value = useMemo(() => {
    return {
      initialFilters,
      units,
      setUnits,
      clusters,
      setClusters,
      zones,
      setZones,
      resetFilters,
    };
  }, [initialFilters, units, clusters, zones, resetFilters]);

  return (
    <SearchFiltersContext.Provider value={value}>
      {children}
    </SearchFiltersContext.Provider>
  );
};

export const useSearchFilters = () => {
  const ctx = useContext(SearchFiltersContext);

  if (!ctx) {
    throw new Error(
      "useSearchFilters must be used within a SearchFiltersContextProvider"
    );
  }

  return ctx;
};
